'use client';

import { useMemo } from 'react';
import { useParams, useSearchParams } from 'next/navigation';
import useSWR, { mutate } from 'swr';
import { usePathname } from 'i18n/routing';
import { PersonalizedCartPayload } from 'sdk/composable-commerce/types/payloads/CartPayloads';
import { revalidateOptions } from 'frontastic';
import { useAccount, useCart } from 'frontastic/hooks';
import { UsePersonalizedCartBannerOptions, UsePersonalizedCartBannerReturn } from './types';

/**
 * Standard UUID format (8-4-4-4-12 hex characters)
 * Example: 16c9ae0c-ae40-49db-b043-cac06dca13a9
 */
export const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Validates whether a given ID string matches the required UUID format.
 */
export const isValidUUID = (id?: string | null): id is string => {
  if (!id || typeof id !== 'string') return false;
  return UUID_REGEX.test(id.trim());
};

/**
 * Builds the SWR cache key for an accountId query.
 */
export const getAccountPersonalizedCartBannerKey = (accountId: string) => [
  '/action/cart/getPersonalizedCartBanner',
  'account',
  accountId.trim(),
];

/**
 * Matches any SWR cache key targeting the accountId personalized cart banner.
 */
export const isAccountPersonalizedCartBannerKey = (key: unknown): boolean =>
  Array.isArray(key) && key[0] === '/action/cart/getPersonalizedCartBanner' && key[1] === 'account';

/**
 * Invalidates the accountId API cache for personalized cart banners using SWR mutate.
 */
export const invalidateAccountPersonalizedBanner = (accountId?: string | null) => {
  if (isValidUUID(accountId)) {
    mutate(getAccountPersonalizedCartBannerKey(accountId), undefined, { revalidate: true });
  }
  return mutate(isAccountPersonalizedCartBannerKey, undefined, { revalidate: true });
};

export interface PageKeyAndPayloadParams {
  pathname?: string | null;
  slug?: string | string[] | null;
  searchParams?: { get: (name: string) => string | null } | null;
  accountId?: string | null;
}

/**
 * Pure helper function to inspect route context and determine the payload and SWR cache key.
 */
export const getPersonalizedCartBannerKeyAndPayload = ({
  pathname,
  slug,
  searchParams,
  accountId,
}: PageKeyAndPayloadParams): {
  payload: PersonalizedCartPayload | null;
  key: (string | undefined)[] | null;
} => {
  const cleanPathname = pathname || '';
  const slugArray = Array.isArray(slug) ? slug : typeof slug === 'string' ? [slug] : [];

  // 1. Thank-You Page: e.g. /thank-you?order-id=9982b207-bcd1-4c0b-a043-3da8ea35d52f
  const isThankYouPage =
    cleanPathname.includes('/thank-you') || cleanPathname.endsWith('/thank-you') || slugArray.includes('thank-you');

  if (isThankYouPage) {
    const orderId = searchParams?.get('order-id') || searchParams?.get('orderId');
    if (isValidUUID(orderId)) {
      const validOrderId = orderId.trim();
      return {
        payload: {
          query: {
            orderId: validOrderId,
          },
        },
        key: ['/action/cart/getPersonalizedCartBanner', 'order', validOrderId],
      };
    }
    // On thank-you page: if orderId is missing or invalid, do not call
    return { payload: null, key: null };
  }

  // 2. Add to Cart Confirmation Page:
  // e.g. /addtocartconfirmation/1952278e-8aae-4e2e-ba61-6e37ef2e2d38/lineitem/2af766f8-3232-488c-a833-74f34fb9fb58
  const isAddToCartConfirmationPage =
    cleanPathname.includes('/addtocartconfirmation') || slugArray.includes('addtocartconfirmation');

  if (isAddToCartConfirmationPage) {
    let cartId: string | undefined | null;
    let lineItemId: string | undefined | null;

    const match = new RegExp(/\/addtocartconfirmation\/([^/]+)\/lineitem\/([^/?#]+)/i).exec(cleanPathname);
    if (match) {
      cartId = match[1];
      lineItemId = match[2];
    } else {
      const addIndex = slugArray.indexOf('addtocartconfirmation');
      if (addIndex !== -1 && slugArray.length > addIndex + 1) {
        cartId = slugArray[addIndex + 1];
        const lineItemIndex = slugArray.indexOf('lineitem', addIndex + 1);
        if (lineItemIndex !== -1 && slugArray.length > lineItemIndex + 1) {
          lineItemId = slugArray[lineItemIndex + 1];
        } else if (slugArray.length > addIndex + 3) {
          lineItemId = slugArray[addIndex + 3];
        }
      }
    }

    if (isValidUUID(cartId) && isValidUUID(lineItemId)) {
      const validCartId = cartId.trim();
      const validLineItemId = lineItemId.trim();
      return {
        payload: {
          query: {
            cartId: validCartId,
            lineItemId: validLineItemId,
          },
        },
        key: ['/action/cart/getPersonalizedCartBanner', 'cartLineItem', validCartId, validLineItemId],
      };
    }
    // On add to cart confirmation page: if either ID is missing or invalid, do not call
    return { payload: null, key: null };
  }

  // 3. Rest of the pages: use accountId from useAccount
  if (isValidUUID(accountId)) {
    const validAccountId = accountId.trim();
    return {
      payload: {
        query: {
          accountId: validAccountId,
        },
      },
      key: ['/action/cart/getPersonalizedCartBanner', 'account', validAccountId],
    };
  }

  // Not matching UUID or guest user: do not call
  return { payload: null, key: null };
};

/**
 * Custom hook that fetches the personalized cart banner based on page context
 * with UUID validation and SWR caching / request deduplication.
 */
const usePersonalizedCartBanner = <T = any>(
  options?: UsePersonalizedCartBannerOptions,
): UsePersonalizedCartBannerReturn<T> => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const params = useParams();
  const slug = params?.slug as string | string[] | undefined;

  const { account } = useAccount();
  const accountId = account?.accountId;

  const { getPersonalizedCartBanner } = useCart();

  const { payload, key } = useMemo(() => {
    if (options?.overridePayload) {
      const override = options.overridePayload;
      if ('orderId' in override.query && isValidUUID(override.query.orderId)) {
        const validOrderId = override.query.orderId.trim();
        return {
          payload: { query: { orderId: validOrderId } },
          key: ['/action/cart/getPersonalizedCartBanner', 'order', validOrderId],
        };
      }
      if (
        'cartId' in override.query &&
        'lineItemId' in override.query &&
        isValidUUID(override.query.cartId) &&
        isValidUUID(override.query.lineItemId)
      ) {
        const validCartId = override.query.cartId.trim();
        const validLineItemId = override.query.lineItemId.trim();
        return {
          payload: { query: { cartId: validCartId, lineItemId: validLineItemId } },
          key: ['/action/cart/getPersonalizedCartBanner', 'cartLineItem', validCartId, validLineItemId],
        };
      }
      if ('accountId' in override.query && isValidUUID(override.query.accountId)) {
        const validAccountId = override.query.accountId.trim();
        return {
          payload: { query: { accountId: validAccountId } },
          key: ['/action/cart/getPersonalizedCartBanner', 'account', validAccountId],
        };
      }
      return { payload: null, key: null };
    }

    return getPersonalizedCartBannerKeyAndPayload({
      pathname: pathname || '',
      slug,
      searchParams,
      accountId,
    });
  }, [options?.overridePayload, pathname, slug, searchParams, accountId]);

  const swrKey = options?.enabled === false ? null : key;

  const {
    data,
    error,
    isLoading,
    isValidating,
    mutate: boundMutate,
  } = useSWR<T>(
    swrKey,
    async () => {
      if (!payload) return null as unknown as T;
      const res = await getPersonalizedCartBanner(payload);

      // Whenever the addtocart or thank you API calls succeed, invalidate the accountId API using mutate
      if (key?.[1] === 'order' || key?.[1] === 'cartLineItem') {
        invalidateAccountPersonalizedBanner(accountId);
      }

      return res;
    },
    {
      ...revalidateOptions,
      revalidateIfStale: true,
      revalidateOnMount: true,
      ...options?.swrOptions,
    },
  );

  return {
    data,
    error,
    isLoading: Boolean(swrKey && isLoading),
    isValidating,
    mutate: boundMutate,
    payload,
    isValid: Boolean(payload),
    invalidateAccountBanner: () => invalidateAccountPersonalizedBanner(accountId),
  };
};

export default usePersonalizedCartBanner;
export * from './types';
